import { Controller } from "@hotwired/stimulus"
import consumer from '@channels/consumer'

export default class extends Controller {
  
  static targets = ["users", "links"]

  static values = { 
    debug: { type: Boolean, default: false },
    useIdle: { type: Boolean, default: true }
  }
  
  connect() {

    // Assign this to a controller variable to access it in the consumer
    const controller = this
    
    consumer.subscriptions.create("PresenceChannel", {

      initialized() {
        controller.debugValue && console.log("Initialized: Presence channel")
        this.ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "resize", "wheel", "touchstart"]
        this.INTERVAL_MS = 60_000
        this.activityDetected = false
      },
      
      connected() {
        controller.debugValue && console.log("Connection: Presence channel")
        this.install()
        this.perform("appear", { path: window.location.pathname, title: document.title })
      },
    
      disconnected() {
        controller.debugValue && console.log("Disconnected: Presence channel")
        this.uninstall()
      },
    
      rejected() {
        controller.debugValue && console.log("Rejected: Presence channel")
      },

      received({ type, ...data }) {
        
        controller.debugValue && console.log(`Event received: ${type} for ${data.user.name}`, data)
        
        if (type == "user-subscribe") this.subscribeUser(data.user)
        if (type == "user-login") this.setUser({ ...data, event: type })
        if (type == "user-appear") this.handleAppear(data)
        if (type == "user-pathchange") this.setUser({ ...data, event: type })
        if (type == "user-away") this.setUserAway(data.user)
        if (type == "user-back") this.setUserBack({ ...data, event: type })
        if (type == "user-idle") this.setUserIdle(data.user)
        if (type == "user-active") this.setUserActive({ ...data, event: type })
        if (type == "user-unsubscribe") this.unsubscribeUser(data.user)
        if (type == "user-logout") this.unsubscribeUser(data.user)
      },
      
      // Getters, helpers
      get currentUser() {
        return JSON.parse(document.body.dataset.user)
      },

      getUserRow(userId) {
        if (!controller.hasUsersTarget) return
        return controller.usersTarget.querySelector(`tr[data-id="${userId}"]`)
      },

      getUserLink(userId) {
        if (!controller.hasLinksTarget) return
        return controller.linksTarget.querySelector(`a[data-id="${userId}"]`)
      },

      getUserStatus(userId) {
        const row = this.getUserRow(userId)
        if (!row) return
        const classList = row.querySelector(".indicator-status").classList
        if (classList.contains("indicator-idle")) return "idle"
        if (classList.contains("indicator-away")) return "away"
        return "online"
      },
      
      // Custom event handler events

      pageLoad(e) {
        if (e.detail?.url.includes("/users/sign_in")) return
        controller.debugValue && console.log("Page load: Presence channel", e.detail?.url)
        this.perform("pathchange", { path: e.detail.url, title: document.title })
      },

      handleAppear(data) {
        this.setUser(data)
        this.setUserLink(data.user)        
      },
      
      visibilitychange(e) {
        const visible = !document.hidden
        controller.debugValue && console.log(`Page ${visible ? "visible" : "hidden"}: Presence channel`)
        if (visible) {
          this.interval = setInterval(this.idle.bind(this), this.INTERVAL_MS)
          this.perform("back", { path: window.location.pathname, title: document.title })
        } else {
          clearInterval(this.interval)
          this.perform("away")
        }
      },

      idle() {
        if (!this.activityDetected) return
        this.activityDetected = false
        clearInterval(this.interval)
        this.perform("idle")
        this.setUserStatus(this.currentUser, "idle")
      },
      
      registerActivity() {
        if (this.activityDetected) return
        clearInterval(this.interval)
        this.interval = setInterval(this.idle.bind(this), this.INTERVAL_MS)
        this.activityDetected = true
        this.perform("active", { path: window.location.pathname, title: document.title })
        this.setUserStatus(this.currentUser, "online")
      },

      // Custom methods

      setUser({ user, path, title, event, status = "online" })  {
        if (!controller.hasUsersTarget) return
        
        controller.debugValue && console.log(`Called setUser from event: ${event || "-"}`, { user, path, title, event, status })
        
        const existingRow = this.getUserRow(user.id)
        
        const pathLink = path ? `<a href="${path}">${title || path}</a>` : "-"
        const statusClass = `indicator-${status}`
        const statusText = status == "online" ? "En ligne" : status == "away" ? "Absent" : "Inactif"
        
        const html = `
          <tr data-id="${user.id}">
            <td class="plain user w-[30%]">
              <a href="/utilisateurs/${user.id}">${user.name}</a>
            </td>
            <td class="plain path w-[50%] path">${pathLink}</td>
            <td class="plain status w-[20%]">
              <div class="flex items-center gap-2">
                <span class="status-text text-smaller">${statusText}</span>
                <span class="indicator-status ${statusClass}"></span>
              </div>
            </td>
          </tr>
        `
        if (existingRow) {
          existingRow.innerHTML = html
        } else {
          controller.usersTarget.innerHTML += html
        }
      },

      setUserStatus(user, status) {
        controller.debugValue && console.log(`Setting user status: ${status}`)
        const row = this.getUserRow(user.id)
        if (!row) return
        const statusClass = `indicator-${status}`
        const statusText = status == "online" ? "En ligne" : status == "away" ? "Absent" : "Inactif"
        row.querySelector(".status-text").textContent = statusText
        row.querySelector(".indicator-status").classList.remove("indicator-online", "indicator-away", "indicator-idle")
        row.querySelector(".indicator-status").classList.add(statusClass)
      },

      setUserAway(user) {
        this.setUserStatus(user, "away")
      },
    
      setUserBack({ user, path, title, event }) {
        this.setUser({ user, path, event, title, status: "online" })
      },

      setUserIdle(user) {
        const row = this.getUserRow(user.id)
        // We don't need to set idle status if user is away
        if (row?.querySelector("indicator-status")?.classList.contains("indicator-away")) return
        this.setUserStatus(user, "idle")
      },

      setUserActive({ user, path, title, event }) {
        this.setUser({ user, path, event, title, status: "online" })
        this.setUserLink(user)
      },
      
      subscribeUser(user) {
        this.setUserLink(user)
      },
      
      unsubscribeUser(user) {
        if (!user) return
        this.getUserRow(user.id)?.remove()
        this.removeUserLink(user)
      },

      setUserLink(user) {
        if (!controller.hasLinksTarget) return
        if (this.getUserLink(user.id)) return
        const link = `<a data-id="${user.id}" href="/utilisateurs/${user.id}">${user.name}</a>`
        controller.linksTarget.innerHTML += link
      },

      removeUserLink(user) {
        if (!controller.hasLinksTarget) return
        const link = controller.linksTarget.querySelector(`a[data-id="${user.id}"]`)
        link && link.remove()
      },

      // Setup and teardown

      install() {
        document.addEventListener("turbo:load", this.pageLoad.bind(this));
        document.addEventListener("visibilitychange", this.visibilitychange.bind(this));
        if (controller.useIdleValue) {
          this.interval = setInterval(this.idle.bind(this), this.INTERVAL_MS)
          this.ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, this.registerActivity.bind(this)))
        }
      },
      
      uninstall() {
        document.removeEventListener("turbo:load", this.pageLoad.bind(this));
        document.removeEventListener("visibilitychange", this.visibilitychange.bind(this));
        if (controller.useIdleValue) {
          this.interval && clearInterval(this.interval)
          this.ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, this.registerActivity.bind(this)))
        }
      }      
      
    })
  }

}
